import { ThemeMode, colors, semanticColors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { CookbookProvider } from '@creditornot/cb-provider';
import { useAuthState } from '@creditornot/wolt-auth/react';
import { datadogRum } from '@datadog/browser-rum';
import React, { useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { IntlProvider } from 'react-intl';
import { BrowserRouter, Route } from 'react-router-dom';
import { createGlobalStyle, ThemeProvider } from 'styled-components';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter5Adapter } from 'use-query-params/adapters/react-router-5';

import { useLocalStorageJSON } from 'common/hooks';
import useUser from 'common/hooks/useUser';
import { useLanguage, useMessages, setLanguage } from 'locale';
import { MerchantAdminTheme, createMerchantAdminTheme } from 'merchantAdminTheme';

import App from './App';
import ErrorBoundary from './ErrorBoundary';
import Loading from './Loading';
import { customTelemetryLogin, customTelemetryLogout } from './telemetry';
import { ThemeModeContext } from '../common/themeMode';

const fontFamily =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;";

const GlobalStyles = createGlobalStyle`
html {
  font-family: ${fontFamily};
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

body {
  color: ${cbSemanticCssVars.textPrimary};
  background: ${cbSemanticCssVars.surfaceMain};
  font-family: ${fontFamily};
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

a {
  text-decoration: none;
  cursor: pointer;
  color: ${colors.wolt100};
}
`;

const TOAST_ID = 'translations';

const Root = () => {
  const [themeMode, setThemeMode] = useLocalStorageJSON<ThemeMode>('theme-mode', 'light');
  const { rtl, key: locale, label: localeLabel } = useLanguage();
  const { user } = useUser();
  const { status: authStatus } = useAuthState();

  const direction = rtl ? 'rtl' : 'ltr';
  const merchantAdminTheme: MerchantAdminTheme = useMemo(() => createMerchantAdminTheme(locale), [locale]);

  const { data: messages, error: messagesError } = useMessages(locale, {
    onError: () => {
      toast.error(`Failed to load translations for ${localeLabel}, using English as fallback.`, {
        id: TOAST_ID,
      });
      setLanguage('en');
    },
  });

  const themeModeContext = useMemo(() => ({ themeMode, setThemeMode }), [themeMode, setThemeMode]);

  useEffect(() => {
    if (user) {
      customTelemetryLogin(user);
      datadogRum.setUser({ id: user.id });
    }

    return () => {
      // Reset telemetry user info + metadata
      customTelemetryLogout();
    };
  }, [user]);

  useEffect(() => {
    return () => {
      // When we are transitioning away from the "logged-in" status...
      if (authStatus === 'logged-in') {
        // Clear KYC related data so we prompt KYC again on login.
        window.sessionStorage.removeItem('skipIDVerification');
        window.localStorage.removeItem('pos_integration.venues');

        // Hard reload the page to clear the runtime memory of any sensitive data.
        // Logic in RouteWhenAuthenticated.tsx takes care of redirecting to /login after the reload.
        window.location.reload();
      }
    };
  }, [authStatus]);

  if (!messages && !messagesError) {
    return <Loading />;
  }

  return (
    <IntlProvider locale={locale} messages={messages}>
      <CookbookProvider themeMode={themeMode} direction={direction}>
        <ThemeModeContext.Provider value={themeModeContext}>
          <ThemeProvider
            theme={{
              cbDirection: direction,
              cbThemeMode: themeMode,
              cbSemanticColors: semanticColors[themeMode],

              // FIXME: this can be removed after upgrading to the newer
              // `CookbookProvider` from `@creditornot/cb-provider`.
              ...merchantAdminTheme,
            }}
          >
            <GlobalStyles />
            <ErrorBoundary>
              {/* FIXME: */}
              <BrowserRouter>
                <QueryParamProvider adapter={ReactRouter5Adapter}>
                  <Route component={App} path="/:feature?/:slug?" />
                </QueryParamProvider>
              </BrowserRouter>
            </ErrorBoundary>
          </ThemeProvider>
        </ThemeModeContext.Provider>
      </CookbookProvider>
    </IntlProvider>
  );
};

export default Root;
